import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Check, ChevronUp, ChevronDown, Plus } from 'react-feather';

/**
 * InlineSelect — searchable, portal-rendered table select.
 * Replaces the Tailwind-blue react-select in inline-editing tables.
 *
 * Invisible at rest; surfaces as a bordered control on row hover (rowActive),
 * opens a searchable menu with keyboard nav (↑ ↓ Enter Esc).
 *
 * Options use the react-select-compatible shape { value, label }, plus an optional
 * `hint` — a muted trailing note in the menu row (e.g. the bucket a choice maps to).
 * Hints are searchable but never shown on the closed trigger.
 * onChange is called with the selected value, or null when cleared.
 */
function InlineSelect({
  value,
  options,
  onChange,
  onCreateOption,
  createLabel = 'Create',
  placeholder = 'Assign…',
  rowActive = false,
  disabled = false,
  clearable = true,
  minWidth = 224,
  ariaLabel,
  // Portal menu stacking. The default clears normal page chrome; callers inside
  // a high-z modal (e.g. the Import Review modal at 999999) must raise it or the
  // menu opens behind the modal.
  menuZIndex = 9999,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState(false);
  const trigRef = useRef(null);
  const srchRef = useRef(null);
  const menuRef = useRef(null);
  const [mp, setMp] = useState({});

  const sel = options.find((o) => o.value === value) || null;
  const opts = useMemo(() => {
    if (!q) return options;
    const needle = q.toLowerCase();
    return options.filter(
      (o) =>
        String(o.label).toLowerCase().includes(needle) ||
        (o.hint ? String(o.hint).toLowerCase().includes(needle) : false)
    );
  }, [options, q]);
  const createText = q.trim();
  const hasExactMatch = useMemo(() => {
    if (!createText) return false;
    const needle = createText.toLowerCase();
    return options.some((o) =>
      String(o.label).trim().toLowerCase() === needle ||
      String(o.value).trim().toLowerCase() === needle
    );
  }, [createText, options]);
  const showCreate = Boolean(onCreateOption && createText && !hasExactMatch);
  const menuItemCount = opts.length + (showCreate ? 1 : 0);

  const openMenu = useCallback(() => {
    if (!trigRef.current || disabled) return;
    const r = trigRef.current.getBoundingClientRect();
    const w = Math.max(r.width, minWidth, 280);
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const estH = Math.min(opts.length * 44 + 114 + (sel && clearable ? 46 : 0), 392);
    let left = r.left;
    if (left + w > window.innerWidth - 10) left = window.innerWidth - w - 10;
    setMp({ top: spaceBelow >= estH ? r.bottom + 8 : Math.max(10, r.top - estH - 8), left, width: w });
    setOpen(true);
    setHi(0);
    setTimeout(() => srchRef.current?.focus(), 10);
  }, [clearable, disabled, minWidth, opts.length, sel]);

  const close = useCallback(() => { setOpen(false); setQ(''); }, []);
  const pick = useCallback((v) => { onChange(v); close(); }, [onChange, close]);
  const createOption = useCallback(async () => {
    if (!showCreate || creating) return;
    setCreating(true);
    try {
      const createdValue = await onCreateOption(createText);
      pick(createdValue === undefined ? createText : createdValue);
    } catch (err) {
      console.error('Error creating inline select option:', err);
    } finally {
      setCreating(false);
    }
  }, [createText, creating, onCreateOption, pick, showCreate]);

  useEffect(() => {
    if (menuItemCount === 0) {
      setHi(0);
    } else if (hi >= menuItemCount) {
      setHi(menuItemCount - 1);
    }
  }, [hi, menuItemCount]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!menuRef.current?.contains(e.target) && !trigRef.current?.contains(e.target)) close();
    };
    const onResize = () => close();
    // Close on scroll of an ANCESTOR (so the fixed menu doesn't detach from the
    // trigger), but ignore scrolling within the menu's own option list.
    const onScroll = (e) => { if (menuRef.current?.contains(e.target)) return; close(); };
    document.addEventListener('mousedown', h);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', h);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  const tKey = (e) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) { e.preventDefault(); openMenu(); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };
  const mKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(i + 1, Math.max(menuItemCount - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (opts[hi]) pick(opts[hi].value);
      else if (showCreate && hi === opts.length) createOption();
    }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  const show = (rowActive || open) && !disabled;

  return (
    <div style={{ width: '100%', padding: '0 4px' }}>
      <button
        ref={trigRef}
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={open ? close : openMenu}
        onKeyDown={tKey}
        style={{
          // Fixed height + always-1px border + always-reserved icons keep the
          // control dimensionally identical at rest and on hover, so revealing
          // it never reflows the column (which would cause hover jitter).
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 34, boxSizing: 'border-box',
          padding: '4px 8px', borderRadius: 8, outline: 'none', textAlign: 'left', minWidth: 0,
          cursor: disabled ? 'default' : 'pointer',
          border: `1px solid ${open ? 'var(--lodge-orange-400)' : show ? 'var(--lodge-onyx-200)' : 'transparent'}`,
          background: open ? 'var(--lodge-orange-50)' : 'var(--lodge-white)',
          boxShadow: open ? 'var(--focus)' : 'none',
          transition: 'border-color .12s, background .12s, box-shadow .12s',
        }}
      >
        <span
          style={{
            flex: 1, minWidth: 0, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4,
            color: sel ? 'var(--lodge-onyx-900)' : show ? 'var(--lodge-onyx-400)' : 'var(--lodge-onyx-300)',
          }}
        >
          {/* Constant text (placeholder when empty) so hover only changes color, never width → no column reflow/jitter */}
          {sel ? sel.label : placeholder}
        </span>
        {/* Clear — always reserved when a value exists; fades in on hover (no width change) */}
        {sel && clearable && (
          <span
            role="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onChange(null); }}
            style={{
              padding: 3, borderRadius: 4, display: 'flex', alignItems: 'center', flexShrink: 0, color: 'var(--lodge-onyx-400)',
              opacity: show && !open ? 1 : 0, pointerEvents: show && !open ? 'auto' : 'none',
              cursor: 'pointer', transition: 'opacity .12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lodge-onyx-100)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={12} />
          </span>
        )}
        {/* Chevron — always rendered (reserves width); fades in on hover/open */}
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, opacity: show ? 1 : 0, transition: 'opacity .12s' }}>
          {open
            ? <ChevronUp size={14} color="var(--lodge-orange-400)" />
            : <ChevronDown size={14} color="var(--lodge-onyx-400)" />}
        </span>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', ...mp, zIndex: menuZIndex,
            background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)',
            borderRadius: 14, boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)', overflow: 'hidden',
            animation: 'it-menuIn .12s ease-out',
          }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--lodge-onyx-100)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--lodge-onyx-50)', borderRadius: 10, padding: '10px 12px' }}>
              <Search size={16} color="var(--lodge-onyx-400)" />
              <input
                ref={srchRef}
                type="text"
                value={q}
                placeholder="Search..."
                onChange={(e) => { setQ(e.target.value); setHi(0); }}
                onKeyDown={mKey}
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 14, flex: 1, minWidth: 0, color: 'var(--lodge-onyx-900)', fontFamily: 'var(--font-sans)' }}
              />
            </div>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
            {sel && clearable && (
              <button
                type="button"
                onClick={() => pick(null)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 16px',
                  border: 'none', cursor: 'pointer', fontSize: 14, background: 'transparent',
                  color: 'var(--lodge-onyx-400)', textAlign: 'left',
                  borderBottom: '1px solid var(--lodge-onyx-50)', fontFamily: 'var(--font-sans)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lodge-onyx-50)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <X size={14} color="var(--lodge-onyx-300)" /> Clear
              </button>
            )}
            {opts.length === 0 && !showCreate ? (
              <div style={{ padding: '18px 16px', textAlign: 'center', fontSize: 14, color: 'var(--lodge-onyx-400)' }}>No results</div>
            ) : (
              opts.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o.value)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    width: '100%', padding: '12px 16px', border: 'none', cursor: 'pointer',
                    fontSize: 14, textAlign: 'left', fontFamily: 'var(--font-sans)',
                    background: o.value === value ? 'var(--lodge-orange-50)' : i === hi ? 'var(--lodge-onyx-50)' : 'transparent',
                    color: o.value === value ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-900)',
                    fontWeight: o.value === value ? 700 : 500,
                  }}
                  onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = 'var(--lodge-onyx-50)'; setHi(i); }}
                  onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = i === hi ? 'var(--lodge-onyx-50)' : 'transparent'; }}
                >
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0, flex: 1 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                    {o.hint && (
                      <span
                        style={{
                          marginLeft: 'auto', flexShrink: 0, maxWidth: '45%', fontSize: 12, fontWeight: 500,
                          color: 'var(--lodge-onyx-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {o.hint}
                      </span>
                    )}
                  </span>
                  {o.value === value && <Check size={15} color="var(--lodge-orange-400)" style={{ flexShrink: 0 }} />}
                </button>
              ))
            )}
            {showCreate && (
              <button
                type="button"
                onClick={createOption}
                disabled={creating}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px',
                  border: 'none', borderTop: opts.length > 0 ? '1px solid var(--lodge-onyx-50)' : 'none',
                  cursor: creating ? 'wait' : 'pointer', fontSize: 14, textAlign: 'left', fontFamily: 'var(--font-sans)',
                  background: hi === opts.length ? 'var(--lodge-orange-50)' : 'transparent',
                  color: 'var(--lodge-orange-500)', fontWeight: 700, opacity: creating ? 0.7 : 1,
                }}
                onMouseEnter={() => setHi(opts.length)}
              >
                <Plus size={15} color="var(--lodge-orange-400)" style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {creating ? 'Creating…' : `${createLabel} "${createText}"`}
                </span>
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default InlineSelect;
